import type { EntityType } from "@config";
import { findAncestorRepeatableKey } from "./nesting-depth";
import type { Repeatable } from "./types";

/**
 * One repeatable group in containment order. `parentKey` is DERIVED from the
 * entity tree whenever the group's entity is loaded, and falls back to the
 * `parentKey` recorded on the definition at mark time when it is not (the
 * share dialog can open outside the editor, with no entities in Redux). It is
 * null for a root group — either the group has no repeatable ancestor, or that
 * ancestor's key is not a known group in the same list (e.g. its entity was
 * deleted).
 */
export interface RepeatableTreeNode {
  repeatable: Repeatable;
  parentKey: string | null;
  children: RepeatableTreeNode[];
}

/**
 * Nests `repeatables` by containment: a group whose nearest repeatable ancestor
 * (`findAncestorRepeatableKey`, or the definition's stored `parentKey` when its
 * entity is not loaded) is another group in the list becomes that group's
 * child; everything else is a root. Returns roots only — children are
 * reachable through `children`. Input order is preserved at every level.
 */
export const buildRepeatableTree = (
  repeatables: Repeatable[],
  entities: Record<string, EntityType>,
): RepeatableTreeNode[] => {
  const nodes = repeatables.map<RepeatableTreeNode>((repeatable) => ({
    repeatable,
    parentKey: null,
    children: [],
  }));

  const byKey = new Map<string, RepeatableTreeNode>();
  for (const node of nodes) {
    if (!byKey.has(node.repeatable.key)) byKey.set(node.repeatable.key, node);
  }

  // Parent key per group, ignoring self-references and ancestors that are not
  // groups in this list. Derivation from the entity tree is authoritative; the
  // stored `parentKey` is consulted only when the entity is not loaded.
  const parentOf = new Map<string, string | null>();
  for (const node of nodes) {
    const key = node.repeatable.key;
    if (parentOf.has(key)) continue;
    const derived = entities[node.repeatable.entityId]
      ? findAncestorRepeatableKey(node.repeatable.entityId, entities)
      : undefined;
    const ancestor =
      derived !== undefined ? derived : (node.repeatable.parentKey ?? null);
    parentOf.set(
      key,
      ancestor && ancestor !== key && byKey.has(ancestor) ? ancestor : null,
    );
  }

  // Corrupt entity data could produce a key cycle (a ⊃ b and b ⊃ a); every
  // member of such a cycle is treated as a root so no group is lost.
  for (const key of Array.from(parentOf.keys())) {
    const seen = new Set<string>([key]);
    let currentKey = parentOf.get(key) ?? null;
    while (currentKey) {
      if (seen.has(currentKey)) {
        parentOf.set(key, null);
        break;
      }
      seen.add(currentKey);
      currentKey = parentOf.get(currentKey) ?? null;
    }
  }

  const roots: RepeatableTreeNode[] = [];
  for (const node of nodes) {
    const parentKey = parentOf.get(node.repeatable.key) ?? null;
    node.parentKey = parentKey;
    const parent = parentKey ? byKey.get(parentKey) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
};

/**
 * Drops every node whose own entity is missing from `entities`, together with
 * its children (a deleted container takes the groups inside it with it).
 *
 * Only meaningful when a deck IS loaded: then the entity tree is the truth, so
 * a group whose entity is gone was deleted, and `buildRepeatableTree`'s stored
 * `parentKey` fallback would otherwise resurrect it as a child of its old
 * parent — rendering a phantom sub-card inside every parent row and seeding
 * its key into new rows. With no deck loaded the stored `parentKey` is the
 * ONLY nesting information available, so callers must not prune (see
 * `getRepeatableTree`).
 *
 * Returns the SAME array reference when nothing was pruned, at every level, so
 * `getRepeatableTree`'s `resultEqualityCheck` and its identity tests still see
 * an untouched tree as untouched.
 */
export const pruneOrphanRepeatableNodes = (
  roots: RepeatableTreeNode[],
  entities: Record<string, EntityType>,
): RepeatableTreeNode[] => {
  const pruneLevel = (nodes: RepeatableTreeNode[]): RepeatableTreeNode[] => {
    const kept: RepeatableTreeNode[] = [];
    let unchanged = true;
    for (const node of nodes) {
      if (!entities[node.repeatable.entityId]) {
        unchanged = false;
        continue;
      }
      const children = pruneLevel(node.children);
      if (children === node.children) kept.push(node);
      else {
        unchanged = false;
        kept.push({ ...node, children });
      }
    }
    return unchanged ? nodes : kept;
  };
  return pruneLevel(roots);
};

/**
 * Structural equality for two repeatable trees: same shape, same keys, same
 * `parentKey`, same group definitions (by identity, else by serialized value)
 * at every level. Used as the `resultEqualityCheck` of `getRepeatableTree` so
 * a deck mutation that leaves the nesting untouched keeps the PREVIOUS array
 * reference — `buildRepeatableTree` allocates a fresh array on every entity
 * change, and the share dialog resets its variable form whenever the derived
 * `defaultValues` identity changes (`edit-version.tsx`), which would discard
 * whatever the user is typing.
 */
export const areRepeatableTreesEqual = (
  a: RepeatableTreeNode[],
  b: RepeatableTreeNode[],
): boolean => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const left = a[i];
    const right = b[i];
    if (left === right) continue;
    if (left.parentKey !== right.parentKey) return false;
    if (left.repeatable.key !== right.repeatable.key) return false;
    if (
      left.repeatable !== right.repeatable &&
      JSON.stringify(left.repeatable) !== JSON.stringify(right.repeatable)
    ) {
      return false;
    }
    if (!areRepeatableTreesEqual(left.children, right.children)) return false;
  }
  return true;
};

/** The node for `key` anywhere in the tree, or undefined when unknown. */
export const findTreeNode = (
  roots: RepeatableTreeNode[],
  key: string,
): RepeatableTreeNode | undefined => {
  for (const node of roots) {
    if (node.repeatable.key === key) return node;
    const found = findTreeNode(node.children, key);
    if (found) return found;
  }
  return undefined;
};

/**
 * Key of the outermost group containing `key` (the key itself when it is
 * already a root); undefined when `key` is not in the tree.
 */
export const rootKeyOf = (
  roots: RepeatableTreeNode[],
  key: string,
): string | undefined => {
  let node = findTreeNode(roots, key);
  if (!node) return undefined;
  const seen = new Set<string>([key]);
  while (node.parentKey && !seen.has(node.parentKey)) {
    seen.add(node.parentKey);
    const parent = findTreeNode(roots, node.parentKey);
    if (!parent) break;
    node = parent;
  }
  return node.repeatable.key;
};

/**
 * Repeatable keys enclosing `entityId`, OUTERMOST first, including the
 * entity's OWN `settings.repeatableKey` as the innermost key. Empty when the
 * entity is not inside (and is not itself) a repeatable group. Cycle-safe like
 * `countRepeatableAncestors`.
 */
export const getEntityRepeatableChain = (
  entityId: string,
  entities: Record<string, EntityType>,
): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();
  let currentId: string | null | undefined = entities[entityId]
    ? entityId
    : null;
  while (currentId && entities[currentId] && !seen.has(currentId)) {
    seen.add(currentId);
    const key = entities[currentId].settings?.repeatableKey;
    if (typeof key === "string" && key) keys.push(key);
    currentId = entities[currentId].parentId;
  }
  return keys.reverse();
};

/**
 * Flattens a nested chain onto top-level keys so a nested group's fields
 * resolve while editing row 1 of each parent: for `["team", "skills"]` the
 * result is `{ ...data, skills: data.team?.[0]?.skills ?? data.skills }`, and
 * for `["a", "b", "c"]` each key is lifted from row 1 of the key before it,
 * falling back to the flat key when the parent row carries no nested array.
 *
 * Returns `data` itself (same reference) for a chain shorter than 2 keys or
 * when there is no data.
 */
export const scopeVersionDataForChain = (
  data: Record<string, unknown> | undefined | null,
  chain: string[],
): Record<string, unknown> | undefined => {
  if (data == null || chain.length < 2) return data ?? undefined;

  const out: Record<string, unknown> = { ...data };
  let currentRows: unknown = data[chain[0]];
  for (let i = 1; i < chain.length; i++) {
    const key = chain[i];
    const firstRow = Array.isArray(currentRows) ? currentRows[0] : undefined;
    const fromParent =
      firstRow && typeof firstRow === "object"
        ? (firstRow as Record<string, unknown>)[key]
        : undefined;
    out[key] = Array.isArray(fromParent) ? fromParent : data[key];
    currentRows = out[key];
  }
  return out;
};

/**
 * Where a pill named `name` (`"<key>.<field>"`) should open the repeatable
 * editor: the root group that owns the data, plus the key whose rows are in
 * focus. Null when the first segment is not a known repeatable key — ordinary
 * fast-vars and dotted integration names (`hubspot.contact.email`) fall
 * through untouched.
 */
export const repeatablePillTarget = (
  name: string,
  roots: RepeatableTreeNode[],
): { rootKey: string; focusKey: string } | null => {
  const focusKey = name.split(".")[0];
  if (!focusKey || !findTreeNode(roots, focusKey)) return null;
  const rootKey = rootKeyOf(roots, focusKey);
  return rootKey ? { rootKey, focusKey } : null;
};

/**
 * The `focusKey` to hand `openRepeatableRowsDialog` for a pill target:
 * undefined when the pill belongs to the root group itself (nothing nested to
 * focus), the nested key otherwise.
 */
export const repeatablePillFocus = (target: {
  rootKey: string;
  focusKey: string;
}): string | undefined =>
  target.focusKey === target.rootKey ? undefined : target.focusKey;
